import { defineStore } from "pinia";
import {
    isNumberWithDecimal,
    validateDecimal,
    validateNumberBetweenZeroAndOnethousand,
    validateNumberGreaterOrEqualThanZero,
} from "../libs/number.helper";
import { fetchData, postData } from "../libs/axios";

const ACTIVITY_SERVICE_URL = "/api/activity/v1/org/pricing";
const PRICING_STORAGE_KEY = "pricingStoreData";

const namedPriceErrorMessage = {
    name: "Name is required.",
    value: "Price is required.",
    validPrice: "Please enter a valid price!",
    nameAndValue: "Name and price are required.",
};
const errorMessages = {
    sellingTicket: "Please select one option.",
    sellingTicketAgreement: "You must agree to the terms and conditions in order to continue.",
    spaceAvailable: "Enter how many spaces are available to sell on Loopla.",
    spaceAvailableWhenSellingOn: "Please add at least one space or set this event to not selling tickets",
    spaceAvailableNotValidNumber: "Enter a valid number of spaces",
    isFree: "You must select if the event is free.",
    basePrice: "Enter a price.",
    validPrice: "Please enter a valid price!",
    recurringOptions: "Please select at least one.",
    namedPriceList: namedPriceErrorMessage,
    singleNamedPriceList: namedPriceErrorMessage,
    dailyNamedPriceList: namedPriceErrorMessage,
    weeklyPriceList: namedPriceErrorMessage,
};

export const PRICING_TICKET_TYPES = {
    SINGLE: "single",
    TASTER: "taster",
    DAILY: "daily",
    WEEKLY: "weekly",
    MONTHLY: "monthly",
};
export const PRICING_RECURRING_TYPES = [
    PRICING_TICKET_TYPES.TASTER,
    PRICING_TICKET_TYPES.DAILY,
    PRICING_TICKET_TYPES.SINGLE,
    PRICING_TICKET_TYPES.WEEKLY,
    PRICING_TICKET_TYPES.MONTHLY,
];
const namedPriceListValidation = (namedPriceList) => {
    const namedPriceListErrors = {};

    namedPriceList.forEach((item, index) => {
        if (item.name.trim() === "") {
            namedPriceListErrors[index] = namedPriceListErrors[index] || {};
            namedPriceListErrors[index].name = namedPriceErrorMessage.name;
        }

        if (item.value.trim() === ""
            || !validateNumberGreaterOrEqualThanZero(item.value)) {
            namedPriceListErrors[index] = namedPriceListErrors[index] || {};
            namedPriceListErrors[index].value = namedPriceErrorMessage.value;
        } else if (
            !validateNumberBetweenZeroAndOnethousand(item.value)
            || !validateDecimal(item.value)
        ) {
            namedPriceListErrors[index] = namedPriceListErrors[index] || {};
            namedPriceListErrors[index].value = namedPriceErrorMessage.validPrice;
        }
        return null;
    });

    return Object.keys(namedPriceListErrors).length === 0 ? false : namedPriceListErrors;
};
const baseAndNamedPriceValidation = ({
    hasNamedPriceList,
    basePrice,
    namedPriceList,
    basePriceErrorKey,
    namedPriceErrorKey,
}) => {
    const errors = {};
    /* Base price validation */
    if (hasNamedPriceList === false) {
        if (basePrice.trim() === ""
            || !validateNumberGreaterOrEqualThanZero(basePrice)
            || !validateNumberBetweenZeroAndOnethousand(basePrice)
            || !validateDecimal(basePrice)
        ) {
            errors[basePriceErrorKey] = errorMessages.validPrice;
        }
    }
    /* Named price validation */
    if (hasNamedPriceList === true) {
        const namedPriceErrors = namedPriceListValidation(
            namedPriceList,
        );

        if (namedPriceErrors) {
            errors[namedPriceErrorKey] = namedPriceErrors;
        }
    }
    return errors;
};
export const usePricingStore = defineStore("pricing", {
    state: () => ({
        sellingTicket: undefined,
        sellingTicketAgreement: false,
        isFree: undefined,
        spaceAvailable: "0",
        isRecurring: undefined,
        recurringType: undefined,
        timetableId: null,
        prices: {
            single: {
                isActive: false,
                basePrice: "",
                hasNamedPriceList: false,
                namedPriceList: [],
            },
            taster: {
                isActive: false,
                type: "free",
                paidPrice: "",
            },
            daily: {
                isActive: false,
                basePrice: "",
                hasNamedPriceList: false,
                namedPriceList: [],
                proRata: false,
            },
            weekly: {
                isActive: false,
                basePrice: "",
                hasNamedPriceList: false,
                namedPriceList: [],
                proRata: false,
            },
        },
        immutablePrices: {},
        errors: {},
    }),
    actions: {
        async getLocalStorage(storage) {
            const storageData = await storage?.getStorageSync(PRICING_STORAGE_KEY);
            if (!!storageData
                && Object.keys(storageData).length !== 0
                && storageData.constructor === Object) {
                this.$state = storageData;
            }
        },

        async setLocalStorage(storage) {
            await storage?.setStorageSync(PRICING_STORAGE_KEY, this.$state);
        },

        async removeLocalStorage(storage) {
            await storage?.removeStorageSync(PRICING_STORAGE_KEY);
        },

        setRecurringType(type) {
            switch (type) {
            case "day":
            case "daily":
                if (this.$state.recurringType === PRICING_TICKET_TYPES.DAILY) {
                    return;
                }
                if (this.$state.isRecurring === false) {
                    this.resetOnSellingTicketChange();
                }
                if (this.$state.recurringType === PRICING_TICKET_TYPES.WEEKLY) {
                    this.$state.prices.weekly = {
                        isActive: false,
                        basePrice: "",
                        hasNamedPriceList: false,
                        namedPriceList: [],
                        proRata: false,
                    };
                }
                this.$state.recurringType = PRICING_TICKET_TYPES.DAILY;
                this.$state.isRecurring = true;
                break;
            case "week":
            case "weekly":
                if (this.$state.recurringType === PRICING_TICKET_TYPES.WEEKLY) {
                    return;
                }
                if (this.$state.isRecurring === false) {
                    this.resetOnSellingTicketChange();
                }
                if (this.$state.recurringType === PRICING_TICKET_TYPES.DAILY) {
                    this.$state.prices.daily = {
                        isActive: false,
                        basePrice: "",
                        hasNamedPriceList: false,
                        namedPriceList: [],
                        proRata: false,
                    };
                }
                this.$state.recurringType = PRICING_TICKET_TYPES.WEEKLY;
                this.$state.isRecurring = true;
                break;
            default:
                if (this.$state.isRecurring === true) {
                    this.resetOnSellingTicketChange();
                }
                this.$state.recurringType = undefined;
                this.$state.isRecurring = false;
                break;
            }
        },

        togglePricingOption(option) {
            this.$state.prices[option].isActive = !this.$state.prices[option].isActive;
        },

        validate() {
            this.$state.errors = {};
            if (this.$state.sellingTicket === undefined) {
                this.$state.errors.sellingTicket = errorMessages.sellingTicket;
                return false;
            }

            /* Agreement validation */
            if (this.$state.sellingTicket && this.$state.sellingTicketAgreement === false) {
                this.$state.errors.sellingTicketAgreement = errorMessages.sellingTicketAgreement;
                return false;
            }

            /* Available spaces validation */
            if (this.$state.sellingTicket
                && !validateNumberGreaterOrEqualThanZero(this.$state.spaceAvailable)) {
                this.$state.errors.spaceAvailable = errorMessages.spaceAvailable;
            } else if (this.$state.sellingTicket
                && (!validateNumberBetweenZeroAndOnethousand(this.$state.spaceAvailable)
                || !isNumberWithDecimal(this.$state.spaceAvailable))
            ) {
                this.$state.errors.spaceAvailable = errorMessages.spaceAvailableNotValidNumber;
            }
            /* Check if sell on loopla is true and the capacity is zero
             should make error */
            if (this.$state.sellingTicket && this.$state.spaceAvailable === "0") {
                this.$state.errors.spaceAvailable = errorMessages.spaceAvailableWhenSellingOn;
            }

            /* Event is FREE validation */
            if (this.$state.isFree === undefined) {
                this.$state.errors.isFree = errorMessages.isFree;
                return false;
            }

            /* Not recurring event pricing validation */
            if (!this.$state.isRecurring && this.$state.isFree === false) {
                this.$state.errors = {
                    ...this.$state.errors,
                    ...baseAndNamedPriceValidation({
                        ...this.$state.prices.single,
                        basePriceErrorKey: "oneOffBasePrice",
                        namedPriceErrorKey: "oneOffNamedPriceList",
                    }),
                };
            }

            /* Recurring event pricing validation */
            if (this.$state.isRecurring && this.$state.isFree === false) {
                /* At least one recurring option selected validation */
                if (!this.$state.prices.taster.isActive
                    && !this.$state.prices.single.isActive
                    && !this.$state.prices.daily.isActive
                    && !this.$state.prices.weekly.isActive
                ) {
                    this.$state.errors.recurringOptions = errorMessages.recurringOptions;
                }
                /* Taster validation */
                /* Tester have to option free and paid, the validation required when the type is paid */
                if (this.$state.prices.taster.isActive === true && this.$state.prices.taster.type === "paid") {
                    this.$state.errors = {
                        ...this.$state.errors,
                        ...baseAndNamedPriceValidation({
                            hasNamedPriceList: false,
                            basePrice: this.$state.prices.taster.paidPrice,
                            namedPriceList: [],
                            basePriceErrorKey: "tasterPaidPrice",
                            namedPriceErrorKey: "",
                        }),
                    };
                }

                /* Single  validation */
                if (this.$state.prices.single.isActive === true) {
                    this.$state.errors = {
                        ...this.$state.errors,
                        ...baseAndNamedPriceValidation({
                            ...this.$state.prices.single,
                            basePriceErrorKey: "singleBasePrice",
                            namedPriceErrorKey: "singleNamedPriceList",
                        }),
                    };
                }

                /* Daily  validation */
                if (this.$state.prices.daily.isActive === true) {
                    this.$state.errors = {
                        ...this.$state.errors,
                        ...baseAndNamedPriceValidation({
                            ...this.$state.prices.daily,
                            basePriceErrorKey: "dailyBasePrice",
                            namedPriceErrorKey: "dailyNamedPriceList",
                        }),
                    };
                }

                /* Weekly  validation */
                if (this.$state.prices.weekly.isActive === true) {
                    this.$state.errors = {
                        ...this.$state.errors,
                        ...baseAndNamedPriceValidation({
                            ...this.$state.prices.weekly,
                            basePriceErrorKey: "weeklyBasePrice",
                            namedPriceErrorKey: "weeklyNamedPriceList",
                        }),
                    };
                }
            }
            return Object.keys(this.$state.errors).length === 0;
        },

        resetOnSellingTicketChange() {
            this.$state.sellingTicketAgreement = false;
            this.$state.isFree = undefined;
            this.$state.spaceAvailable = "0";
            this.$state.timetableId = null;
            this.$state.errors = {};
            this.$state.prices = {
                single: {
                    isActive: false,
                    basePrice: "",
                    hasNamedPriceList: false,
                    namedPriceList: [],
                },
                taster: {
                    isActive: false,
                    type: "free",
                    paidPrice: "",
                },
                daily: {
                    isActive: false,
                    basePrice: "",
                    hasNamedPriceList: false,
                    namedPriceList: [],
                    proRata: false,
                },
                weekly: {
                    isActive: false,
                    basePrice: "",
                    hasNamedPriceList: false,
                    namedPriceList: [],
                    proRata: false,
                },
            };
            this.$state.immutablePrices = {};
        },

        async getEventPricing() {
            if (!this.$state.timetableId) {
                return console.error("Timetable ID is required");
            }

            const data = await fetchData(`${ACTIVITY_SERVICE_URL}/${this.$state.timetableId}`);
            this.$state = { ...this.$state, ...this.transformApiDataToStateFormat(data) };
            return data;
        },

        async saveEventPricing(timetableId = null) {
            const pricingTimetableId = timetableId || this.$state.timetableId;
            if (!pricingTimetableId) {
                return console.error("Timetable ID is required");
            }
            if (!this.validate()) {
                return false;
            }
            const data = this.transformStateToApiData(pricingTimetableId);
            try {
                this.setImmutablePrices();
                return await postData(`${ACTIVITY_SERVICE_URL}`, data);
            } catch (error) {
                throw new Error("Error while event pricing saving");
            }
        },

        transformApiDataToStateFormat(data) {
            const formattedData = {};
            formattedData.sellingTicket = data.sell_on_loopla === "1";
            formattedData.sellingTicketAgreement = data.accept_privacy === "1";
            formattedData.spaceAvailable = data.capacity || "0";
            formattedData.isFree = data.free === "1";
            if (data.prices.length === 0) {
                return formattedData;
            }
            formattedData.prices = {
                single: {
                    isActive: false,
                    basePrice: "0",
                    hasNamedPriceList: false,
                    namedPriceList: [],
                },
                taster: {
                    isActive: false,
                    type: "free",
                    paidPrice: "0",
                },
                daily: {
                    isActive: false,
                    basePrice: "0",
                    hasNamedPriceList: false,
                    namedPriceList: [],
                    proRata: false,
                },
                weekly: {
                    isActive: false,
                    basePrice: "0",
                    hasNamedPriceList: false,
                    namedPriceList: [],
                    proRata: false,
                },
            };
            formattedData.isRecurring = data
                .prices
                .find((item) => PRICING_RECURRING_TYPES.includes(item.ticket_type) !== undefined);
            data.prices.forEach((option) => {
                switch (option.ticket_type) {
                case PRICING_TICKET_TYPES.SINGLE:
                case PRICING_TICKET_TYPES.DAILY:
                case PRICING_TICKET_TYPES.WEEKLY:
                    formattedData.prices[option.ticket_type].isActive = true;
                    if (option.ticket_name === null) {
                        formattedData.prices[option.ticket_type].basePrice = option.price;
                        formattedData.prices[option.ticket_type].hasNamedPriceList = false;
                    } else {
                        formattedData.prices[option.ticket_type].hasNamedPriceList = true;
                        formattedData.prices[option.ticket_type].namedPriceList = formattedData
                            .prices[option.ticket_type].namedPriceList || [];
                        formattedData.prices[option.ticket_type].namedPriceList.push({
                            name: option.ticket_name,
                            value: option.price,
                        });
                    }
                    if (option.ticket_type === PRICING_TICKET_TYPES.DAILY
                            || option.ticket_type === PRICING_TICKET_TYPES.WEEKLY) {
                        formattedData.prices[option.ticket_type].proRata = option.pro_rata === 1 || option.pro_rata === "1";
                    }
                    break;
                case PRICING_TICKET_TYPES.TASTER:
                    formattedData.prices.taster.isActive = true;
                    if (option.ticket_name === "free") {
                        formattedData.prices.taster.type = "free";
                    } else {
                        formattedData.prices.taster.type = "paid";
                        formattedData.prices.taster.paidPrice = option.price;
                    }
                    break;
                default:
                    console.error("Unknown ticket type");
                    break;
                }
            });

            return formattedData;
        },

        transformStateToApiData(timetableId) {
            return {
                id: parseInt(timetableId, 10),
                sellOnLoopla: this.$state.sellingTicket,
                acceptPrivacy: this.$state.sellingTicket
                    ? this.$state.sellingTicketAgreement : false,
                capacity: this.$state.sellingTicket
                    ? parseInt(this.$state.spaceAvailable, 10) : 0,
                free: this.$state.isFree,
                prices: this.getPricesFromData(),
            };
        },

        getPricesFromData() {
            if (this.$state.isFree) {
                return [];
            }

            const prices = [];
            if (this.$state.prices.taster.isActive) {
                prices.push({
                    ticketType: "taster",
                    options: this.$state.prices.taster.type === "free" ? [{
                        ticketName: "free",
                        price: 0,
                    }] : [{
                        ticketName: "paid",
                        price: parseFloat(this.$state.prices.taster.paidPrice),
                    }],
                });
            }
            if (this.$state.prices.single.isActive) {
                prices.push(this.getPriceByType(PRICING_TICKET_TYPES.SINGLE));
            }
            if (this.$state.prices.daily.isActive) {
                prices.push(this.getPriceByType(PRICING_TICKET_TYPES.DAILY));
            }
            if (this.$state.prices.weekly.isActive) {
                prices.push(this.getPriceByType(PRICING_TICKET_TYPES.WEEKLY));
            }
            return prices;
        },

        getPriceByType(type) {
            const pricingOption = {
                ticketType: PRICING_TICKET_TYPES[type.toUpperCase()],
                options: [],
            };

            if (this.$state.prices[type].proRata) {
                pricingOption.proRata = true;
            }

            if (this.$state.prices[type].hasNamedPriceList === false) {
                pricingOption.options = [{
                    price: parseFloat(this.$state.prices[type].basePrice),
                }];
            } else {
                pricingOption.options = this.$state.prices[type].namedPriceList.map((item) => ({
                    ticketName: item.name,
                    price: parseFloat(item.value),
                }));
            }
            return pricingOption;
        },

        async reset(storage = null) {
            this.$state.sellingTicket = undefined;
            this.$state.isRecurring = undefined;
            this.$state.recurringType = undefined;
            this.resetOnSellingTicketChange();
            if (storage) {
                await this.removeLocalStorage(storage);
            }
        },

        async resetAndFillWithDataForEdit(data, storage = null) {
            await this.reset(storage);
            this.$state = { ...this.$state, ...data };
        },

        setSellingTicket(value) {
            this.$state.sellingTicket = value === "true";
        },

        setIsFree(value) {
            this.resetError();
            if (value === "false" && !this.$state.isRecurring) {
                this.togglePricingOption("single");
            }
            this.$state.isFree = value === "true";
        },

        setImmutablePrices(sourceData = null) {
            let replaceData = sourceData;
            if (!sourceData) {
                replaceData = this.$state;
            }
            this.$state.immutablePrices = {
                sellingTicket: replaceData.sellingTicket,
                sellingTicketAgreement: replaceData.sellingTicketAgreement,
                isFree: replaceData.isFree,
                spaceAvailable: replaceData.spaceAvailable,
                isRecurring: replaceData.isRecurring,
                recurringType: replaceData.recurringType,
                timetableId: replaceData.timetableId,
                prices: {
                    single: { ...replaceData.prices?.single },
                    taster: { ...replaceData.prices?.taster },
                    daily: { ...replaceData.prices?.daily },
                    weekly: { ...replaceData.prices?.weekly },
                },
            };
        },
        resetError() {
            this.$state.errors = {};
        },
    },
    getters: {
        getPricingState: (state) => state,
        getLowestSinglePrice: (state) => {
            if (state.prices.single.hasNamedPriceList) {
                return state.prices.single.namedPriceList
                    .reduce((prev, current) => (prev.value < current.value ? prev : current))
                    .value;
            }
            return state.prices.single.basePrice;
        },
        getLowestRecurringPrice: (state) => {
            if (state.prices.daily.isActive) {
                if (state.prices.daily.hasNamedPriceList) {
                    return state.prices.daily.namedPriceList
                        .reduce(
                            (prev, current) => (prev.value < current.value ? prev : current),
                        ).value;
                }
                return state.prices.daily.basePrice;
            }
            if (state.prices.weekly.isActive) {
                if (state.prices.weekly.hasNamedPriceList) {
                    return state.prices.weekly.namedPriceList
                        .reduce(
                            (prev, current) => (prev.value < current.value ? prev : current),
                        ).value;
                }
                return state.prices.weekly.basePrice;
            }
            return "";
        },
        getOldPricingFormat: (state) => {
            const notFreeRecurring = !state.isFree && state.isRecurring;

            // Free trial check
            const freeTrial = notFreeRecurring
            && state.prices.taster.isActive
            && state.prices.taster.type === "free" ? "yes" : "no";

            let price = "0";
            // Check Event is not free
            if (!state.free) {
                // Check has a single price
                if (state.prices.single.isActive) {
                    price = state.getLowestSinglePrice;
                } else {
                    const notActiveRecurringPrice = !state.prices.daily.isActive
                        && !state.prices.weekly.isActive;
                    // Check has no recurring price and have a paid taster
                    if (notActiveRecurringPrice && state.prices.taster.isActive && state.prices.taster.type === "paid") {
                        price = state.prices.taster.paidPrice;
                    }
                }
            }

            // If it has a recurring price
            const pricePerTerm = notFreeRecurring
            && (state.prices.daily || state.prices.weekly) ? state.getLowestRecurringPrice : "";

            // In every case "per-month" except for weekly that is per-curse
            const priceType = notFreeRecurring
            && state.prices.weekly.isActive ? "per-course" : "per-month"; // "per-month"

            return {
                freePricing: state.isFree,
                freeTrial,
                priceData: {
                    errors: "",
                    price,
                    pricePerTerm,
                    priceType,
                },
            };
        },
    },
});
